import type { JobHeader, TenantRow } from '../schemas/bulk-migration';

/**
 * Reading a job's rows end to end, for the two things that need all of them:
 * the JSON download, and the planned ids a launch resolves "all migratable"
 * into.
 */

/** 200 is the runner's maximum page. */
export const FULL_READ_PAGE_SIZE = 200;
/** A circuit breaker, not a size decision: 200 × 200 rows is past any platform. */
export const MAX_PAGES = 200;

export interface RowPage {
    readonly rows: readonly TenantRow[];
    readonly nextCursor: string | null;
}

/**
 * Every page, in order, until the cursor is `null`. A repeated cursor or more
 * than {@link MAX_PAGES} pages is refused: either is an upstream loop.
 */
export async function collectAllRows(
    fetchPage: (cursor: string | undefined) => Promise<RowPage>,
    maxPages = MAX_PAGES,
): Promise<TenantRow[]> {
    const rows: TenantRow[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined;

    for (let page = 0; page < maxPages; page += 1) {
        const answer = await fetchPage(cursor);
        rows.push(...answer.rows);
        if (answer.nextCursor === null) return rows;
        if (seen.has(answer.nextCursor)) {
            throw new Error('The runner repeated a page cursor; stopped reading.');
        }
        seen.add(answer.nextCursor);
        cursor = answer.nextCursor;
    }
    throw new Error(`Stopped after ${String(maxPages)} pages without reaching the end.`);
}

export function exportFileName(job: Pick<JobHeader, 'moduleId' | 'mode' | 'reportId'>): string {
    return `${job.moduleId}-${job.mode}-${job.reportId}.json`;
}

/** The download: the header as read, and every row. */
export function exportDocument(job: JobHeader, rows: readonly TenantRow[]): string {
    return JSON.stringify({ job, rows }, null, 2);
}

/** Case-insensitive search over the loaded rows: name, slug, tenant id, org id. */
export function searchRows(rows: readonly TenantRow[], search: string): TenantRow[] {
    const term = search.trim().toLowerCase();
    if (term === '') return [...rows];
    return rows.filter(({ tenant }) =>
        [tenant.displayName, tenant.slug, tenant.tenantId, tenant.orgId].some((field) =>
            field?.toLowerCase().includes(term),
        ),
    );
}
